import API from "@services/root/rootApi";
import {
  MonitoredDeviceListOfMonitoredRoom,
  Room,
  RoomsFilters,
  RoomsCount,
  MonitoredRoom,
  RoomCertification,
  RoomComment,
  Attachment,
} from "./types";
import { Dispatch, SetStateAction } from "react";
import { FlattenedRoom } from "@pages/Assets/RoomProvisioning/const";
export const getRooms = async (
  maxResults?: number,
  orderBy?: string,
  startIndex?: number,
  filters?: RoomsFilters,
  queryType = "monitoring"
): Promise<MonitoredDeviceListOfMonitoredRoom> => {
  const excludeSimpleAssets = Boolean(filters?.excludeSimpleAssets);
  delete filters?.excludeSimpleAssets;

  const response = await API.post<MonitoredDeviceListOfMonitoredRoom>(
    `/symphony-api/${queryType}/room`,
    {
      ...filters,
      // Backend uses a different key when provisioning for some reason
      locationId:
        queryType === "provisioning" ? filters?.["location.id"] : undefined,
    },
    {
      params: {
        maxResults: maxResults,
        orderBy: orderBy,
        startIndex: startIndex,
        excludeSimpleAssets: excludeSimpleAssets,
      },
    }
  );

  return response.data;
};

export const getRoomsCount = async (
  filters?: RoomsFilters
): Promise<RoomsCount> => {
  const response = await API.post<RoomsCount>(
    `/symphony-api/monitoring/room/count`,
    filters
  );

  return response.data;
};

export const getMonitoringRoom = async (
  id?: string,
  locationDetails?: boolean,
  excludeSimpleAssets?: boolean
): Promise<MonitoredRoom> => {
  const response = await API.get<MonitoredRoom>(
    `/symphony-api/monitoring/room/${id}${
      locationDetails ? "?expand=location.details" : ""
    }`,
    {
      params: {
        includeUnprovisionedDevices: true,
        excludeSimpleAssets: excludeSimpleAssets === true,
      },
    }
  );

  return response.data;
};

export const getRoom = async (id?: string): Promise<Room> => {
  const response = await API.get<Room>(`/symphony-api/provisioning/room/${id}`);

  return response.data;
};

export const deleteRooms = async (ids: Room["id"][]) =>
  await API.delete(`/symphony-api/provisioning/room`, { data: ids });

export const putRoom = async (
  room: Omit<Room, "id"> & Partial<Pick<Room, "id">>
) =>
  await API.put<string>(
    `/symphony-api/provisioning/room${
      room.id !== undefined ? `/${room.id}` : ""
    }`,
    room
  );
export const addRoom = async (room: Room) =>
  await API.put(`/symphony-api/provisioning/room`, { ...room });

export const updateRoom = async (id: Room["id"], room: Room) =>
  await API.put(`/symphony-api/provisioning/room/${id}`, { ...room });

export const updateRoomCertification = async (
  id: Room["id"],
  certification: RoomCertification
) =>
  await API.put(`/symphony-api/provisioning/room/${id}/certification`, {
    ...certification,
  });

export const updateRoomComment = async (id: Room["id"], comment: RoomComment) =>
  await API.put(`/symphony-api/provisioning/room/${id}/notes`, { ...comment });

export const getRoomAttachments = async (
  id: string,
  accountId: string
): Promise<Attachment[]> => {
  const response = await API.get<Attachment[]>(
    `/symphony-api/files/account/${accountId}/room/${id}/attachments`
  );

  return response.data;
};

export const getRoomAttachment = async (
  accountId: string,
  entityType: string,
  entityId: string,
  id: string
) => {
  const response = await API.get(
    `/symphony-api/files/account/${accountId}/${entityType}/${entityId}/attachments/${id}`,
    {
      responseType: "blob",
    }
  );
  return response.data;
};

export const deleteRoomAttachments = async (
  accountId: string,
  entityId: string,
  ids: Attachment["id"][]
) =>
  await API.delete(
    `/symphony-api/files/account/${accountId}/room/${entityId}/attachments`,
    {
      data: ids,
    }
  );

export const uploadRoomAttachment = async (
  data: FormData,
  objectId: string,
  setProgress: Dispatch<SetStateAction<number>>
) => {
  const response = await API.post(
    `/symphony-api/files/account/${objectId}/attachments`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const progress = (progressEvent.progress ?? 0) * 100;
        setProgress(progress);
      },
    }
  );
  return response.data;
};

export const getRoomsCommonProperties = async (id: Array<Room["id"]>) =>
  (
    await API.post<Partial<FlattenedRoom>>(
      "symphony-api/provisioning/room/commonproperties",
      { id }
    )
  ).data;

export const setRoomsCommonProperties = async (
  id: Array<Room["id"]>,
  field: Partial<FlattenedRoom>
) =>
  (
    await API.put<Partial<FlattenedRoom>>(
      "symphony-api/provisioning/room/commonproperties",
      { id, field }
    )
  ).data;

export const setRoomsProperties = async (
  rooms: Array<Partial<FlattenedRoom> & Required<Pick<FlattenedRoom, "id">>>
) => await API.put<never>("symphony-api/provisioning/room/properties", rooms);
